import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  FileText,
  Clock,
  Award,
  Download,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import Header from './Header';

// Using relative URLs
const API = '/api';

// Helper function to format framework names
const formatFrameworkName = (frameworkCode) => {
  const frameworkMap = {
    'SOC2': 'SOC 2',
    'SOC2_TYPE1': 'SOC 2 Type 1',
    'SOC2_TYPE2': 'SOC 2 Type 2',
    'ISO27001': 'ISO 27001',
    'GDPR': 'GDPR',
    'PCI_DSS': 'PCI DSS',
    'HIPAA': 'HIPAA'
  };
  
  return frameworkMap[frameworkCode?.trim()] || frameworkCode?.trim() || '';
};

const AssessmentReport = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSessionAndReport();
  }, [sessionId]);

  const fetchSessionAndReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch session details
      const sessionResponse = await axios.get(`${API}/voice/session/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSession(sessionResponse.data);

      // Check if analysis exists, if not trigger it
      try {
        const reportResponse = await axios.get(`${API}/voice/session/${sessionId}/report`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setReport(reportResponse.data);
      } catch (reportError) {
        // Report not ready, trigger analysis
        if (reportError.response?.status === 400) {
          console.log('Triggering assessment analysis...');
          try {
            await axios.post(`${API}/voice/session/${sessionId}/analyze`, {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Fetch report again after analysis
            const reportResponse = await axios.get(`${API}/voice/session/${sessionId}/report`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setReport(reportResponse.data);
          } catch (analysisError) {
            console.error('Error analyzing assessment:', analysisError);
            setError('Failed to analyze assessment');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching assessment:', err);
      setError('Failed to load assessment details');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/voice/session/${sessionId}/download-report`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `compliance_report_${sessionId.slice(0, 8)}.pdf`; // fallback
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF report');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-navy-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading assessment report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">Error Loading Report</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>

        {/* Completion Status */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-900">Assessment Completed</h2>
                <p className="text-green-700">Your compliance assessment has been successfully recorded</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Session Details */}
        {session && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-navy-600" />
                Session Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Framework</p>
                  <div className="flex flex-wrap gap-2">
                    {session.framework?.split(',').map((fw, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-700">
                        {formatFrameworkName(fw)}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Duration</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span className="font-semibold text-slate-900">
                      {formatDuration(session.total_duration_seconds || 0)}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Session ID</p>
                  <p className="text-xs font-mono text-slate-500">{session.session_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Status</p>
                  {/* Always show "Completed" on report page - if report exists, assessment is done */}
                  <Badge className="bg-green-100 text-green-700">
                    Completed
                  </Badge>
                </div>
              </div>

              {session.transcript && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900 mb-2">Conversation Transcript</p>
                  <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {(session.transcript || 'No transcript available')
                        .replace(/\bassistant:/gi, 'Nova:')
                        .replace(/\buser:/gi, 'User:')}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Analysis Results */}
        {report && report.analyses && Object.keys(report.analyses).map((framework, idx) => {
          const analysis = report.analyses[framework];
          const overallScore = analysis.overall_score || 0;
          const scoreColor = overallScore >= 80 ? 'text-green-600' : overallScore >= 60 ? 'text-amber-600' : 'text-red-600';
          const scoreBg = overallScore >= 80 ? 'bg-green-50' : overallScore >= 60 ? 'bg-amber-50' : 'bg-red-50';
          
          return (
            <Card key={idx} className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{formatFrameworkName(framework)} Assessment</span>
                  <div className={`text-4xl font-bold ${scoreColor}`}>
                    {overallScore}/100
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                {analysis.summary && (
                  <div className={`p-4 rounded-lg ${scoreBg}`}>
                    <p className="text-sm text-slate-700">{analysis.summary}</p>
                  </div>
                )}

                {/* Category Scores */}
                {analysis.category_scores && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Category Scores</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {Object.entries(analysis.category_scores).map(([category, score]) => (
                        <div key={category} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-700">{category}</span>
                          <span className={`font-semibold ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                            {score}/100
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths */}
                {analysis.strengths && analysis.strengths.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Key Strengths
                    </h4>
                    <ul className="space-y-2">
                      {analysis.strengths.map((strength, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weaknesses */}
                {analysis.weaknesses && analysis.weaknesses.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-amber-600" />
                      Areas for Improvement
                    </h4>
                    <ul className="space-y-2">
                      {analysis.weaknesses.map((weakness, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <span>{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Recommendations</h4>
                    <div className="space-y-2">
                      {analysis.recommendations.map((rec, i) => {
                        const priorityColor = rec.priority === 'High' ? 'bg-red-100 text-red-700' : 
                                             rec.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 
                                             'bg-blue-100 text-blue-700';
                        return (
                          <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                            <Badge className={`${priorityColor} text-xs`}>{rec.priority}</Badge>
                            <span className="text-sm text-slate-700 flex-1">{rec.item}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Actions */}
        <div className="mt-6 flex gap-3 justify-end">
          {report && report.analyses && (
            <Button
              onClick={downloadPDF}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download PDF Report
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/assessment/voice')}
            className="flex items-center gap-2"
          >
            Start New Assessment
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-navy-600 hover:bg-navy-700 text-white"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentReport;
